import Vue from 'vue'
import Vuex from 'vuex'
import { login, logout, getAuth, reloadAuth } from '@/api/admin'
import { getToken, setToken, removeToken } from '@/utils/auth'

Vue.use(Vuex)

const AUTH_CACHE_KEY = 'qs_admin_auth'

function readAuthCache (token) {
  if (!token) return null
  try {
    const raw = sessionStorage.getItem(AUTH_CACHE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!data || data.token !== token) return null
    return data
  } catch (e) {
    return null
  }
}

function writeAuthCache (token, payload) {
  if (!token) return
  try {
    sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({
      token,
      adminId: payload.adminId,
      actions: payload.actions || [],
      permVersion: payload.permVersion == null ? 0 : payload.permVersion
    }))
  } catch (e) {
    // ignore quota
  }
}

function clearAuthCache () {
  try {
    sessionStorage.removeItem(AUTH_CACHE_KEY)
  } catch (e) {
    // ignore
  }
}

export default new Vuex.Store({
  state: {
    token: getToken(),
    adminId: null,
    actions: [],
    permVersion: 0
  },
  getters: {
    token: state => state.token,
    actions: state => state.actions,
    permVersion: state => state.permVersion,
    hasAction: state => code => {
      if (!code) return true
      const list = state.actions || []
      if (list.includes(code)) return true
      const prefix = code + ':'
      return list.some(a => typeof a === 'string' && a.startsWith(prefix))
    }
  },
  mutations: {
    SET_TOKEN (state, token) {
      state.token = token
    },
    SET_ADMIN_ID (state, id) {
      state.adminId = id
    },
    SET_ACTIONS (state, actions) {
      state.actions = actions || []
    },
    SET_PERM_VERSION (state, v) {
      state.permVersion = v == null ? 0 : Number(v)
    },
    APPLY_AUTH (state, payload) {
      state.adminId = payload.adminId
      state.actions = payload.actions || []
      state.permVersion = payload.permVersion == null ? 0 : Number(payload.permVersion)
    }
  },
  actions: {
    async Login ({ commit }, { userName, passWord }) {
      const res = await login(userName, passWord)
      const data = res.data || {}
      const token = data.token
      if (!token) {
        return Promise.reject(new Error('登录响应缺少 token'))
      }
      setToken(token)
      commit('SET_TOKEN', token)
      // 登录响应已带权限，先落盘，刷新页也能秒开菜单
      if (data.actions) {
        const auth = {
          adminId: data.admin && data.admin.id,
          actions: data.actions,
          permVersion: data.permVersion
        }
        commit('APPLY_AUTH', auth)
        writeAuthCache(token, auth)
      }
      return res
    },
    /**
     * 刷新/进站加载权限：
     * 1) 先读 sessionStorage（瞬时）
     * 2) 再打 /admin/auth（Redis 命中通常毫秒级）
     * 3) permVersion 变了才认为权限变更
     * 返回 { actions, changed }
     */
    async GetAuth ({ commit, state }) {
      const token = state.token || getToken()
      const cached = readAuthCache(token)
      if (cached && (!state.actions || !state.actions.length)) {
        commit('APPLY_AUTH', cached)
      }
      const res = await getAuth()
      const data = res.data || {}
      const next = {
        adminId: data.adminId,
        actions: data.actions || [],
        permVersion: data.permVersion == null ? 0 : Number(data.permVersion)
      }
      const changed = !cached ||
        Number(cached.permVersion) !== next.permVersion ||
        JSON.stringify(cached.actions || []) !== JSON.stringify(next.actions)
      commit('APPLY_AUTH', next)
      writeAuthCache(token, next)
      return { actions: next.actions, changed, permVersion: next.permVersion }
    },
    /** 手动刷新：清服务端缓存 + 本地缓存，再拉一遍 */
    async ReloadAuth ({ commit, state }) {
      const res = await reloadAuth()
      const data = res.data || {}
      const next = {
        adminId: data.adminId,
        actions: data.actions || [],
        permVersion: data.permVersion == null ? 0 : Number(data.permVersion)
      }
      commit('APPLY_AUTH', next)
      writeAuthCache(state.token || getToken(), next)
      return next.actions
    },
    async Logout ({ commit }) {
      try {
        await logout()
      } catch (e) {
        // ignore
      }
      removeToken()
      clearAuthCache()
      commit('SET_TOKEN', '')
      commit('SET_ADMIN_ID', null)
      commit('SET_ACTIONS', [])
      commit('SET_PERM_VERSION', 0)
    }
  }
})
